import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Modal,
  Animated,
  PanResponder,
  Platform
} from "react-native";
import Glob from "src/globalConstants";
import NavBar from "src/components/navBar";
import { MaterialIcons } from "@expo/vector-icons";
import moment from "moment";
import Database from "src/backend/database";
import Analytics from "src/backend/analytics";
import Util from "src/utility";
import Rex from "src/globalState";
import { Skeleton } from "native-base";
import ChildCard from "src/components/ChildCard";
import ConfettiCannon from "react-native-confetti-cannon";
import * as Haptics from "expo-haptics";

const { width } = Glob.get("dimensions");

export default function PaymentsCustomerInvoiceDetails({ route, navigation }) {
  const {
    invoice,
    customerProfile,
    onPaymentSuccess,
    isPaidInvoice
  } = route.params;
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(invoice.status);
  const [children, setChildren] = useState({});
  const [classrooms, setClassrooms] = useState({});
  const [isLoadingChild, setIsLoadingChild] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const slideAnimation = useState(new Animated.Value(0))[0];
  const progressAnimation = useState(new Animated.Value(0))[0];
  const [expandedItems, setExpandedItems] = useState({});

  // Check if invoice has a child attached
  const tcChildID = invoice?.metadata?.tcChildID;

  const isOverdue =
    !isPaidInvoice &&
    invoice.due_date &&
    moment.unix(invoice.due_date).isBefore(moment(), "day");

  // Determine status text and color based on invoice state
  const getStatusColor = () => {
    if (paymentStatus === "paid" || isPaidInvoice) return "#2DD881";
    if (isOverdue) return "#EF4444";
    return "#F6AD55";
  };

  const getStatusText = () => {
    if (isPaidInvoice) return "Paid";
    if (isOverdue) return "Overdue";
    return paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1);
  };

  const orderedInvoiceItems = Util.getOrderedInvoiceItems(invoice?.lines?.data);

  const organizationName = Rex.getConfig()?.names?.nickname;

  useEffect(() => {
    Analytics.logEvent("view_paymentsCustomerInvoiceDetails");
  }, []);

  useEffect(() => {
    if (tcChildID && Rex.getConfig()?.transparentClassroomIntegrationEnabled) {
      fetchChildData();
    }
  }, [tcChildID]);

  const fetchChildData = async () => {
    try {
      setIsLoadingChild(true);
      const response = await Database.tcCheckIntegrationStatus();
      const { status } = response || {};
      const connected = status === Glob.get("tcIntegrationStatuses").CONNECTED;

      if (connected) {
        const childrenResponse = await Database.tcFetchChildren();
        const childrenByID = {};
        (childrenResponse?.data || []).forEach((child) => {
          childrenByID[child.id] = child;
        });
        setChildren(childrenByID);

        // Fetch classrooms if needed
        if (childrenByID[tcChildID]?.classroom_ids?.length > 0) {
          const classroomsResponse = await Database.tcFetchClassrooms();
          const classroomsByID = {};
          (classroomsResponse?.data || []).forEach((classroom) => {
            classroomsByID[classroom.id] = classroom;
          });
          setClassrooms(classroomsByID);
        }
      }
    } catch (error) {
      console.error("Error fetching child data:", error);
    } finally {
      setIsLoadingChild(false);
    }
  };

  // Calculate discount information
  const hasDiscounts = invoice.discounts && invoice.discounts.length > 0;
  const subtotal = invoice.subtotal || 0;
  const total = isPaidInvoice ? invoice.amount_paid : invoice.amount_due;
  const discountAmount = subtotal > total ? subtotal - total : 0;

  // Format discount description
  const getDiscountDescription = (discount) => {
    if (discount.coupon) {
      if (discount.coupon.percent_off) {
        return `${discount.coupon.percent_off}% off`;
      }
      if (discount.coupon.amount_off) {
        return `$${(discount.coupon.amount_off / 100).toFixed(2)} off`;
      }
    }
    return "Discount applied";
  };

  const openInvoiceURL = (url) => {
    if (url) Util.openURL(url);
  };

  // Get dynamic colors
  const buttonColor = Rex.getConfig()?.colors?.button;

  // Progress animation for the slider track
  const progressWidth = progressAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: ["0%", "100%"]
  });

  // Create pan responder for the slider
  const panResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onPanResponderMove: (_, gestureState) => {
      // Limit the movement between 0 and 250
      const newValue = Math.max(0, Math.min(250, gestureState.dx));
      slideAnimation.setValue(newValue);

      // Update progress animation (0 to 1 based on slider position)
      progressAnimation.setValue(newValue / 250);
    },
    onPanResponderRelease: (_, gestureState) => {
      // If slider moved more than 200px, trigger payment
      if (gestureState.dx > 200) {
        if (Platform.OS !== "web") {
          Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
        }

        Animated.timing(slideAnimation, {
          toValue: 250,
          duration: 200,
          useNativeDriver: true
        }).start();

        Animated.timing(progressAnimation, {
          toValue: 1,
          duration: 200,
          useNativeDriver: false
        }).start(() => {
          processPayment();
        });
      } else {
        // Reset slider position
        Animated.spring(slideAnimation, {
          toValue: 0,
          useNativeDriver: true
        }).start();

        Animated.spring(progressAnimation, {
          toValue: 0,
          useNativeDriver: false
        }).start();
      }
    }
  });

  const handlePayInvoice = async () => {
    Analytics.logEvent("touch_paymentsCustomerInvoiceDetails_pay");
    setShowConfirmation(true);
  };

  const processPayment = async () => {
    setIsProcessingPayment(true);

    try {
      const response = await Database.stripeConnectedAccountPayInvoice({
        invoiceId: invoice.id
      });

      if (response.success) {
        // First show success in the modal
        if (Platform.OS !== "web") {
          Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        }

        // Show success animation
        setShowConfirmation(false);
        setShowConfetti(true);

        // Call the callback to refresh the dashboard
        if (onPaymentSuccess) {
          onPaymentSuccess();
        }
        setPaymentStatus("paid");

        // Hide confetti after 3 seconds
        setTimeout(() => {
          setShowConfetti(false);
        }, 3000);
      } else {
        setShowConfirmation(false);
        Alert.alert(
          "Payment Failed",
          response.message || "There was an error processing your payment."
        );
      }
    } catch (error) {
      console.error("Error paying invoice:", error);
      setShowConfirmation(false);
      Alert.alert(
        "Payment Error",
        "There was an error processing your payment. Please try again."
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Check if customer has a default payment method
  const hasDefaultPaymentMethod = !!customerProfile?.invoice_settings
    ?.default_payment_method;
  // Determine if we should show the Pay Now button
  const isAutoPaymentInvoice = !invoice.due_date;
  const showPayNowButton =
    paymentStatus !== "paid" &&
    hasDefaultPaymentMethod &&
    !isAutoPaymentInvoice &&
    !isPaidInvoice;

  // Get the child data
  const child = tcChildID ? children[tcChildID] : null;

  // Render child section
  const renderChildSection = () => {
    if (!tcChildID) return null;

    if (isLoadingChild) {
      return (
        <View style={styles.childSection}>
          <Text style={styles.childSectionTitle}>Child Information</Text>
          <View style={styles.childSkeletonContainer}>
            <Skeleton size="10" rounded="full" style={{ marginRight: 12 }} />
            <View style={{ flex: 1 }}>
              <Skeleton.Text lines={2} />
            </View>
          </View>
        </View>
      );
    }

    if (child) {
      return (
        <View style={styles.childSection}>
          <Text style={styles.childSectionTitle}>Child Information</Text>
          <ChildCard child={child} classrooms={classrooms} />
        </View>
      );
    }

    return null;
  };

  return (
    <View style={styles.pageContent}>
      <NavBar navigation={navigation} text="Invoice Details" />
      <ScrollView style={styles.scrollView}>
        {isOverdue && (
          <View style={styles.overdueAlert}>
            <View style={styles.overdueIconContainer}>
              <MaterialIcons name="warning" size={24} color="white" />
            </View>
            <View style={styles.overdueTextContainer}>
              <Text style={styles.overdueAlertTitle}>Invoice Overdue</Text>
              <Text style={styles.overdueAlertText}>
                This invoice was due on{" "}
                {moment.unix(invoice.due_date).format("MMM D, YYYY")}
              </Text>
            </View>
          </View>
        )}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Invoice Summary</Text>
          <View style={styles.detailRow}>
            <Text style={styles.label}>Status</Text>
            <Text style={[styles.value, { color: getStatusColor() }]}>
              {getStatusText()}
            </Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.label}>Amount</Text>
            <Text style={styles.value}>
              $
              {(
                (isPaidInvoice ? invoice.amount_paid : invoice.amount_due) / 100
              ).toFixed(2)}
            </Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.label}>Created</Text>
            <Text style={styles.value}>
              {moment(invoice.created * 1000).format("MMM D, YYYY")}
            </Text>
          </View>
          {isPaidInvoice && (
            <View style={styles.detailRow}>
              <Text style={styles.label}>Payment Date</Text>
              <Text style={styles.value}>
                {moment(invoice.status_transitions?.paid_at * 1000).format(
                  "MMM D, YYYY"
                )}
              </Text>
            </View>
          )}
          {!isPaidInvoice && invoice.due_date && (
            <View style={styles.detailRow}>
              <Text style={styles.label}>Due Date</Text>
              <Text style={[styles.value, isOverdue && styles.overdueValue]}>
                {moment(invoice.due_date * 1000).format("MMM D, YYYY")}
              </Text>
            </View>
          )}
          {!isPaidInvoice && !invoice.due_date && (
            <View style={styles.detailRow}>
              <Text style={styles.label}>Payment</Text>
              <Text style={[styles.value, styles.autoPaymentText]}>
                Auto-payment
                {invoice.next_payment_attempt
                  ? ` on ${moment(invoice.next_payment_attempt * 1000).format(
                      "MMM D, YYYY"
                    )}`
                  : ""}
              </Text>
            </View>
          )}
          {isPaidInvoice && invoice.number && (
            <View style={styles.detailRow}>
              <Text style={styles.label}>Invoice Number</Text>
              <Text style={styles.value}>{invoice.number}</Text>
            </View>
          )}
        </View>

        {/* Child information section */}
        {tcChildID && renderChildSection()}

        {/* Discounts section */}
        {hasDiscounts && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Discounts Applied</Text>
            {invoice.discounts.map((discount, index) => (
              <View key={index} style={styles.discountItem}>
                <View style={styles.discountDetails}>
                  <Text style={styles.discountName}>
                    {discount.coupon?.name || "Discount"}
                  </Text>
                  <Text
                    style={[styles.discountDescription, { color: buttonColor }]}
                  >
                    {getDiscountDescription(discount)}
                  </Text>
                </View>
              </View>
            ))}
            {discountAmount > 0 && (
              <View style={styles.savingsSummary}>
                <Text style={styles.savingsText}>
                  You saved ${(discountAmount / 100).toFixed(2)} with applied
                  discounts
                </Text>
              </View>
            )}
          </View>
        )}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Line Items</Text>
          {orderedInvoiceItems.map((item, index) => (
            <View key={index}>
              <TouchableOpacity
                style={styles.lineItem}
                onPress={() => {
                  if (
                    item.metadata?.type === "serviceFee" ||
                    item.metadata?.type === "achDiscount"
                  ) {
                    setExpandedItems((prev) => ({
                      ...prev,
                      [index]: !prev[index]
                    }));
                  }
                }}
                disabled={
                  item.metadata?.type !== "serviceFee" &&
                  item.metadata?.type !== "achDiscount"
                }
              >
                <View style={styles.descriptionContainer}>
                  <Text style={styles.itemDescription}>{item.description}</Text>
                  {(item.metadata?.type === "serviceFee" ||
                    item.metadata?.type === "achDiscount") && (
                    <MaterialIcons
                      name="info-outline"
                      size={16}
                      color="#4A5568"
                      style={styles.infoIcon}
                    />
                  )}
                </View>
                <Text
                  style={[
                    styles.itemAmount,
                    item.amount < 0 && styles.discountAmount
                  ]}
                >
                  {item.amount < 0 ? (
                    <>-${Math.abs((item?.amount || 0) / 100).toFixed(2)}</>
                  ) : (
                    <>${((item?.amount || 0) / 100).toFixed(2)}</>
                  )}
                </Text>
              </TouchableOpacity>

              {item.metadata?.type === "serviceFee" && expandedItems[index] && (
                <View style={styles.expandedInfo}>
                  <Text style={styles.expandedText}>
                    This{" "}
                    {(parseFloat(item.metadata?.percent || "0") * 100).toFixed(
                      1
                    )}
                    % service fee goes directly to {organizationName} to help
                    them cover their costs.
                  </Text>
                </View>
              )}

              {item.metadata?.type === "achDiscount" && expandedItems[index] && (
                <View style={styles.expandedInfo}>
                  <Text style={styles.expandedText}>
                    {organizationName} gave you this{" "}
                    {(parseFloat(item.metadata?.percent || "0") * 100).toFixed(
                      1
                    )}
                    % discount as a small "thank you" for using ACH bank
                    payments as your default payment method.
                  </Text>
                </View>
              )}
            </View>
          ))}
        </View>

        <View style={styles.actions}>
          {showPayNowButton && (
            <TouchableOpacity
              style={[
                styles.payNowButton,
                isProcessingPayment && styles.disabledButton
              ]}
              onPress={handlePayInvoice}
              disabled={isProcessingPayment}
            >
              {isProcessingPayment ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <>
                  <MaterialIcons name="payment" size={20} color="white" />
                  <Text style={styles.payNowButtonText}>Pay Now</Text>
                </>
              )}
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => openInvoiceURL(invoice.hosted_invoice_url)}
          >
            <MaterialIcons name="receipt" size={20} color="#4A5568" />
            <Text style={styles.actionButtonText}>View Invoice Online</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => openInvoiceURL(invoice.invoice_pdf)}
          >
            <MaterialIcons name="picture-as-pdf" size={20} color="#4A5568" />
            <Text style={styles.actionButtonText}>Download PDF</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Payment Confirmation Modal */}
      <Modal
        visible={showConfirmation}
        transparent
        animationType="fade"
        onRequestClose={() => setShowConfirmation(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.confirmationCard}>
            <View style={styles.confirmationHeader}>
              <Text style={styles.confirmationTitle}>Complete Payment</Text>
              <TouchableOpacity
                onPress={() => setShowConfirmation(false)}
                style={styles.closeButton}
              >
                <MaterialIcons name="close" size={24} color="#718096" />
              </TouchableOpacity>
            </View>

            <View style={styles.paymentDetails}>
              <Text style={styles.paymentAmount}>
                ${(invoice.amount_due / 100).toFixed(2)}
              </Text>
              <Text style={styles.paymentDescription}>
                Invoice #{invoice.number || invoice.id.slice(-8)}
              </Text>
            </View>

            {/* Web version - Button */}
            {Platform.OS === "web" ? (
              <View style={styles.webButtonContainer}>
                {isProcessingPayment ? (
                  <View style={styles.processingContainer}>
                    <ActivityIndicator size="large" color={buttonColor} />
                    <Text style={styles.processingText}>
                      Processing payment...
                    </Text>
                  </View>
                ) : (
                  <TouchableOpacity
                    style={[
                      styles.webPayButton,
                      { backgroundColor: buttonColor }
                    ]}
                    onPress={processPayment}
                  >
                    <MaterialIcons
                      name="attach-money"
                      size={24}
                      color="white"
                    />
                    <Text style={styles.webPayButtonText}>Confirm Payment</Text>
                  </TouchableOpacity>
                )}
              </View>
            ) : (
              /* Mobile version */
              <View style={styles.mobilePaymentContainer}>
                {isProcessingPayment ? (
                  <View style={styles.mobileProcessingContainer}>
                    <ActivityIndicator
                      size="large"
                      color={buttonColor}
                      style={styles.processingSpinner}
                    />
                    <Text style={styles.mobileProcessingText}>
                      Processing payment...
                    </Text>
                  </View>
                ) : (
                  <View style={styles.sliderContainer}>
                    <View style={styles.sliderTrack}>
                      <Animated.View
                        style={[
                          styles.sliderProgress,
                          { width: progressWidth, backgroundColor: buttonColor }
                        ]}
                      />
                      <View style={styles.sliderTextContainer}>
                        <MaterialIcons
                          name="arrow-forward"
                          size={16}
                          color="#718096"
                        />
                        <Text style={styles.sliderInnerText}>
                          Slide to confirm payment
                        </Text>
                      </View>
                    </View>
                    <Animated.View
                      style={[
                        styles.sliderThumb,
                        {
                          transform: [{ translateX: slideAnimation }],
                          backgroundColor: buttonColor
                        }
                      ]}
                      {...panResponder.panHandlers}
                    >
                      <MaterialIcons
                        name="attach-money"
                        size={28}
                        color="white"
                      />
                    </Animated.View>
                  </View>
                )}
              </View>
            )}

            <View style={styles.securitySection}>
              <MaterialIcons name="lock" size={16} color="#718096" />
              <Text style={styles.secureText}>Secure Payment</Text>
            </View>
          </View>
        </View>
      </Modal>

      {/* Confetti celebration after successful payment */}
      {showConfetti && (
        <ConfettiCannon
          count={150}
          origin={{ x: width / 2, y: 0 }}
          autoStart
          fadeOut
          explosionSpeed={350}
          fallSpeed={3000}
          colors={["#2DD881", buttonColor, "#4299E1", "#F6AD55"]}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  pageContent: {
    flex: 1,
    alignItems: "center",
    backgroundColor: "#F7FAFC"
  },
  scrollView: {
    flex: 1,
    padding: 16,
    width
  },
  section: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2D3748",
    marginBottom: 16
  },
  detailRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12
  },
  label: {
    color: "#718096",
    fontSize: 16
  },
  value: {
    color: "#2D3748",
    fontSize: 16,
    fontWeight: "500"
  },
  lineItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF2F7"
  },
  itemDescription: {
    color: "#2D3748",
    fontSize: 16
  },
  itemAmount: {
    color: "#2D3748",
    fontSize: 16,
    fontWeight: "500"
  },
  actions: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 6,
    backgroundColor: "#EDF2F7",
    marginBottom: 8
  },
  actionButtonText: {
    marginLeft: 8,
    color: "#4A5568",
    fontSize: 16,
    fontWeight: "500"
  },
  payNowButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
    borderRadius: 6,
    backgroundColor: "#2DD881",
    marginBottom: 16
  },
  payNowButtonText: {
    marginLeft: 8,
    color: "white",
    fontSize: 16,
    fontWeight: "500"
  },
  disabledButton: {
    opacity: 0.7
  },
  autoPaymentText: {
    color: "#4299E1"
  },
  discountItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF2F7"
  },
  discountDetails: {
    flex: 1
  },
  discountName: {
    fontSize: 16,
    fontWeight: "500",
    color: "#2D3748"
  },
  discountDescription: {
    fontSize: 14,
    marginTop: 2
  },
  savingsSummary: {
    marginTop: 12,
    padding: 12,
    backgroundColor: "#F0FFF4",
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: "#2DD881"
  },
  savingsText: {
    fontSize: 14,
    color: "#276749"
  },
  childSection: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16
  },
  childSectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2D3748",
    marginBottom: 16
  },
  childSkeletonContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12
  },
  childInfoContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12
  },
  childPhoto: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 16
  },
  childAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16
  },
  avatarText: {
    fontSize: 20,
    fontWeight: "bold",
    color: "white"
  },
  childDetails: {
    flex: 1
  },
  childName: {
    fontSize: 18,
    fontWeight: "600"
  },
  childAge: {
    fontSize: 14,
    color: "#718096",
    marginTop: 4
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20
  },
  confirmationCard: {
    backgroundColor: "white",
    borderRadius: 16,
    padding: 24,
    width: "100%",
    maxWidth: 400,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5
  },
  confirmationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24
  },
  confirmationTitle: {
    fontSize: 24,
    fontWeight: "600",
    color: "#2D3748"
  },
  closeButton: {
    padding: 4,
    borderRadius: 20
  },
  paymentDetails: {
    alignItems: "center",
    marginBottom: 32
  },
  paymentAmount: {
    fontSize: 42,
    fontWeight: "bold",
    color: "#2D3748",
    marginBottom: 8
  },
  paymentDescription: {
    fontSize: 16,
    color: "#718096"
  },
  sliderContainer: {
    height: 80,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 24,
    position: "relative"
  },
  sliderTrack: {
    position: "absolute",
    width: "100%",
    height: 56,
    backgroundColor: "#EDF2F7",
    borderRadius: 28,
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center"
  },
  sliderProgress: {
    position: "absolute",
    height: "100%",
    left: 0,
    borderRadius: 28
  },
  sliderThumb: {
    position: "absolute",
    left: 0,
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: "center",
    alignItems: "center",
    elevation: 5,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    zIndex: 10
  },
  sliderTextContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    zIndex: 1
  },
  sliderInnerText: {
    color: "#718096",
    fontWeight: "500",
    fontSize: 16,
    marginLeft: 8
  },
  processingContainer: {
    alignItems: "center",
    justifyContent: "center",
    height: 56
  },
  processingText: {
    marginTop: 12,
    color: "#718096",
    fontWeight: "500",
    fontSize: 16
  },
  securitySection: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 8
  },
  secureText: {
    textAlign: "center",
    color: "#718096",
    fontSize: 14,
    marginLeft: 6
  },
  webButtonContainer: {
    marginBottom: 24,
    width: "100%"
  },
  webPayButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    height: 56,
    borderRadius: 28,
    width: "100%"
  },
  webPayButtonText: {
    color: "white",
    fontWeight: "600",
    fontSize: 18,
    marginLeft: 8
  },
  mobilePaymentContainer: {
    width: "100%"
  },
  mobileProcessingContainer: {
    height: 80,
    backgroundColor: "#EDF2F7",
    borderRadius: 28,
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 24
  },
  processingSpinner: {
    marginRight: 12
  },
  mobileProcessingText: {
    color: "#718096",
    fontWeight: "500",
    fontSize: 18
  },
  descriptionContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center"
  },
  infoIcon: {
    marginLeft: 6
  },
  expandedInfo: {
    backgroundColor: "#EDF2F7",
    padding: 12,
    borderRadius: 6,
    marginBottom: 12,
    marginTop: -12
  },
  expandedText: {
    fontSize: 14,
    color: "#4A5568",
    lineHeight: 20
  },
  discountAmount: {
    color: "#2DD881",
    fontWeight: "600"
  },
  overdueAlert: {
    flexDirection: "row",
    backgroundColor: "#FEF2F2",
    borderRadius: 8,
    marginBottom: 16,
    padding: 16,
    alignItems: "center",
    borderLeftWidth: 4,
    borderLeftColor: "#EF4444"
  },
  overdueIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#EF4444",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 16
  },
  overdueTextContainer: {
    flex: 1
  },
  overdueAlertTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#EF4444",
    marginBottom: 4
  },
  overdueAlertText: {
    fontSize: 14,
    color: "#4A5568"
  },
  overdueValue: {
    color: "#EF4444",
    fontWeight: "600"
  }
});
